import React, { useEffect, useState } from "react";
import {
  Modal,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Table,
  Tabs,
  Button,
  Select,
  message,
  Alert,
} from "antd";
import { getStatusColor, calculateTotals } from "../../../lib/utils";
import { getDirectusClient } from "../../../lib/directus";
import { readItems, updateItem, createItem, updateItems, readUsers, updateUser } from "@directus/sdk";
import FacturasService from "../../../services/facturas";
import { traerserialesPorFactura, traerserialesPorFacturaCount } from "../../../services/seriales";
import { useDispatch, useSelector } from "react-redux";
import { setCajaStatus } from "../../../features/caja/cajaSlice";
import moment from "moment";

const { Text, Title } = Typography;

const InvoiceModal = ({
  selectedFactura,
  isModalVisible,
  setIsModalVisible,
  setSelectedFactura,
  fetchFacturas,
  validation
}) => {
  const [serialHistory, setSerialHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [facturaDetails, setFacturaDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // Add new states for anulation
  const [isAnularModalVisible, setIsAnularModalVisible] = useState(false);
  const [motivos, setMotivos] = useState([]);
  const [selectedMotivo, setSelectedMotivo] = useState(null);
  const [anularLoading, setAnularLoading] = useState(false);

  // Add caja related states
  const [cajaAbierta, setCajaAbierta] = useState(false);
  const [currentCajaId, setCurrentCajaId] = useState(null);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userdata.id);
  const cajaState = useSelector((state) => state.caja);

  const fetchFacturaDetails = async () => {
    if (!selectedFactura?.id) return;

    setLoadingDetails(true);
    try {
      const facturaDetails = await FacturasService.getFacturaDetails(selectedFactura.id);
      setFacturaDetails(facturaDetails);
    } catch (error) {
      console.error("Error fetching factura details:", error);
      message.error("Error al cargar los detalles de la factura");
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchSerialHistory = async () => {
    if (!selectedFactura?.id) return;

    setLoadingHistory(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems("historial_seriales", {
          fields: [
            "id",
            "tipo",
            "date_created",
            "user_created.first_name",
            "user_created.last_name",
            "seriales.seriales_id.*",
            "seriales.estado",
            "factura_n.id",
            "prestamo.id",
          ],
          filter: {
            factura_n: { id: { _eq: selectedFactura.id } },
          },
          sort: ["-date_created"],
        })
      );
      setSerialHistory(response);
    } catch (error) {
      console.error("Error fetching serial history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Add fetchMotivos function
  const fetchMotivos = async () => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems("motivo_dev_factura", {
          fields: ["*"],
        })
      );
      setMotivos(response);
    } catch (error) {
      console.error("Error fetching motivos:", error);
      message.error("Error al cargar los motivos");
    }
  };

  // Add handleAnularFactura function
  const handleAnularClick = (record) => {
    setSelectedInvoice(record);
    setIsAnularModalVisible(true);
    fetchMotivos();
  };

  const processInBatches = async (items, batchSize, processor) => {
    const results = [];
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch.map(processor));
      results.push(...batchResults);
    }
    return results;
  };

  const handleAnularFactura = async () => {
    if (!cajaAbierta) {
      message.warning('Debe tener una caja abierta para anular facturas');
      return;
    }

    if (!selectedMotivo) {
      message.warning("Por favor seleccione un motivo");
      return;
    }

    setAnularLoading(true);
    try {
      const directus = await getDirectusClient();

      // Get total count of serials for this factura
      const serialCountResponse = await traerserialesPorFacturaCount(selectedFactura.id);
      const totalSerials = serialCountResponse[0]?.countDistinct?.id || 0;
      
      console.log(`Total de seriales a procesar: ${totalSerials}`);

      if (totalSerials === 0) {
        message.warning("No se encontraron seriales para esta factura");
        setAnularLoading(false);
        return;
      }

      // Get all serials data for this factura
      const serialesData = await traerserialesPorFactura(selectedFactura.id);
      const seriales = serialesData.map(serial => serial.id);

      console.log(`Anulando ${seriales.length} seriales`);

      // Use facturaDetails if available, fallback to selectedFactura
      const facturaToUse = facturaDetails || selectedFactura;

      // Create new invoice for return
      const newInvoiceData = {
        consecutivo: `${facturaToUse.consecutivo}`,
        cliente: facturaToUse.cliente?.id,
        vendedor_factura: facturaToUse.vendedor_factura?.id,
        vendedor_prestamo: facturaToUse.vendedor_prestamo?.id,
        bodega: facturaToUse.bodega?.id,
        caja: currentCajaId, // Use current caja
        estado: "devolcion de factura",
        tipo: facturaToUse.tipo,
        motivo: selectedMotivo,
        factura_devolucion: facturaToUse.id,
      };

      // First create the new invoice
      const newInvoiceResponse = await directus.request(
        createItem("facturas", newInvoiceData)
      );

      // Then create the products for the new invoice
      if (newInvoiceResponse.id && facturaToUse.productos) {
        const productosPromises = facturaToUse.productos.map((prod) =>
          directus.request(
            createItem("productos_factura", {
              factura: newInvoiceResponse.id,
              cantidad: prod.cantidad,
              precio_unitario: prod.precio_unitario,
              producto: prod.producto?.id,
              seriales: {
                create: prod.seriales?.map((serial) => ({
                  productos_factura_id: "+",
                  seriales_id: serial.id
                })) || []
              }
            })
          )
        );

        await Promise.all(productosPromises);
      }

      // Process serials in smaller batches to avoid blocking
      const batchSize = 20;

      // Create single history entry for original invoice with all serials
      console.log('Creando historial para factura original...');
      const serialesForHistory = seriales.map(serialId => ({
        historial_seriales_id: "+",
        seriales_id: serialId
      }));

      await directus.request(
        createItem("historial_seriales", {
          tipo: 2,
          factura_n: selectedFactura.id,
          seriales: {
            create: serialesForHistory
          }
        })
      );

      // Create single history entry for new invoice with all serials
      console.log('Creando historial para nueva factura...');
      await directus.request(
        createItem("historial_seriales", {
          tipo: 2,
          factura_n: newInvoiceResponse.id,
          seriales: {
            create: serialesForHistory
          }
        })
      );

      // Update all serials status in batches
      console.log('Actualizando estado de seriales...');
      await processInBatches(seriales, batchSize, (serialId) =>
        directus.request(
          updateItem("seriales", serialId, {
            estado: 1,
            user: null,
            factura: null
          })
        )
      );

      // Update the original invoice status
      await directus.request(
        updateItem("facturas", selectedFactura.id, {
          estado: "devolucion",
          motivo: selectedMotivo
        })
      );

      message.success(`Factura anulada exitosamente. ${seriales.length} seriales procesados.`);
      setIsAnularModalVisible(false);
      setSelectedMotivo(null);
      fetchFacturas();
    } catch (error) {
      console.error("Error anulando factura:", error);
      message.error("Error al anular la factura: " + error.message);
    } finally {
      setAnularLoading(false);
    }
  };

  // Modify checkCajaStatus to update Redux
  const checkCajaStatus = async () => {
    try {
      const directus = await getDirectusClient();
      const today = moment().startOf('day');
      const tomorrow = moment().endOf('day');

      const response = await directus.request(
        readItems('cajas', {
          fields: ['id', 'fecha_apertura', 'estado'],
          filter: {
            vendedor: { 
              id: { _eq: user }
            },
            estado: { _eq: 1 },
            fecha_apertura: {
              _gt: today.toISOString()
            }
          },
          sort: ['-fecha_apertura'],
          limit: 1
        })
      );

      if (response && response.length > 0) {
        const cajaId = response[0].id;
        // Update both local and Redux state
        setCurrentCajaId(cajaId);
        setCajaAbierta(true);
        dispatch(setCajaStatus({
          cajaId: cajaId,
          isOpen: true
        }));
      } else {
        // Reset both local and Redux state
        setCurrentCajaId(null);
        setCajaAbierta(false);
        dispatch(setCajaStatus({
          cajaId: null,
          isOpen: false
        }));
      }
    } catch (error) {
      console.error('Error checking caja status:', error);
      message.error('Error al verificar el estado de la caja');
    }
  };

  // Use useEffect to sync local state with Redux state
  useEffect(() => {
    setCajaAbierta(cajaState.isOpen);
    setCurrentCajaId(cajaState.cajaId);
  }, [cajaState]);

  useEffect(() => {
    if (isModalVisible && selectedFactura) {
      fetchFacturaDetails();
      fetchSerialHistory();
    }
  }, [isModalVisible, selectedFactura]);

  // Check caja status when modal opens
  useEffect(() => {
    if (isModalVisible) {
      checkCajaStatus();
    }
  }, [isModalVisible]);

  const getHistoryTypeText = (tipo) => {
    const types = {
      1: "Facturación",
      2: "Devolución de Factura",
      3: "Préstamo",
      4: "Devolución de Préstamo",
    };
    return types[tipo] || "Otro";
  };

  const InvoiceDetails = () => {
    if (loadingDetails) {
      return <div style={{ textAlign: 'center', padding: '50px' }}>Cargando detalles...</div>;
    }

    if (!facturaDetails) {
      return <div style={{ textAlign: 'center', padding: '50px' }}>No se pudieron cargar los detalles</div>;
    }

    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Card title="Información General">
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Text strong>Estado:</Text>
              <Tag
                color={getStatusColor(facturaDetails.estado)}
                style={{ marginLeft: 8 }}
              >
                {facturaDetails.estado?.toUpperCase() || "N/A"}
              </Tag>
            </Col>
            <Col span={8}>
              <Text strong>Tipo:</Text>
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {facturaDetails.tipo === 1 ? "CONTADO" : "CRÉDITO"}
              </Tag>
            </Col>
            <Col span={8}>
              <Text strong>Fecha:</Text>
              <Text style={{ marginLeft: 8 }}>
                {new Date(facturaDetails.fecha_facturacion).toLocaleString()}
              </Text>
            </Col>
          </Row>
        </Card>

        <Card title="Información del Cliente y Vendedor">
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Title level={5}>Cliente</Title>
              <p>
                <Text strong>Nombre:</Text> {facturaDetails.cliente?.first_name}{" "}
                {facturaDetails.cliente?.last_name}
              </p>
              <p>
                <Text strong>Documento:</Text>{" "}
                {facturaDetails.cliente?.documento}
              </p>
            </Col>
            <Col span={12}>
              <Title level={5}>Vendedor</Title>
              <p>
                <Text strong>Nombre:</Text>{" "}
                {facturaDetails.vendedor_factura?.first_name}{" "}
                {facturaDetails.vendedor_factura?.last_name}
              </p>
              <p>
                <Text strong>Documento:</Text>{" "}
                {facturaDetails.vendedor_factura?.documento}
              </p>
            </Col>
          </Row>
        </Card>

        <Card title="Información de Bodega y Caja">
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <p>
                <Text strong>Bodega:</Text> {facturaDetails.bodega?.nombre}
              </p>
            </Col>
            <Col span={12}>
              <p>
                <Text strong>Caja ID:</Text> {facturaDetails.caja?.id}
              </p>
            </Col>
          </Row>
        </Card>

        <Card title="Productos">
          <Table
            dataSource={facturaDetails.productos}
            rowKey="id"
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ margin: "0 48px" }}>
                <h4>Seriales del Producto</h4>
                <Table
                  dataSource={record.seriales}
                  rowKey="id"
                  columns={[
                    { title: "Serial", dataIndex: "serial", key: "serial" },
                    {
                      title: "Estado",
                      dataIndex: ["estado", "estado"],
                      key: "estado",
                      render: (estado) => (
                        <Tag color={getStatusColor(estado)}>
                          {estado?.toUpperCase() || "N/A"}
                        </Tag>
                      ),
                    },
                  ]}
                  pagination={true}
                />
              </div>
            ),
            rowExpandable: (record) => record.seriales?.length > 0,
          }}
          columns={[
            {
              title: "Producto",
              dataIndex: ["producto", "nombre"],
              key: "producto",
            },
            { title: "Cantidad", dataIndex: "cantidad", key: "cantidad" },
            {
              title: "Precio Unitario",
              dataIndex: "precio_unitario",
              key: "precio_unitario",
              render: (precio) => `$${precio?.toLocaleString() || 0}`,
            },
            {
              title: "Impuesto %",
              dataIndex: ["producto", "grupo", "impuesto"],
              key: "impuesto",
              render: (impuesto) => `${impuesto || 0}%`,
            },
            {
              title: "Subtotal",
              key: "subtotal",
              render: (_, record) => {
                const subtotal =
                  (record.cantidad || 0) * (record.precio_unitario || 0);
                return `$${subtotal.toLocaleString()}`;
              },
            },
          ]}
          pagination={false}
          summary={(pageData) => {
            const totals = calculateTotals(pageData);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Totales:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell>
                  <Text strong>
                    Subtotal: ${totals.subtotal.toLocaleString()}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell>
                  <Text strong>
                    Total con impuestos: ${totals.total.toLocaleString()}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>

        <Card title="Información de Creación">
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <p>
                <Text strong>Creado por:</Text>{" "}
                {facturaDetails.user_created?.first_name}{" "}
                {facturaDetails.user_created?.last_name}
              </p>
            </Col>
            <Col span={12}>
              <p>
                <Text strong>Fecha creación:</Text>{" "}
                {new Date(facturaDetails.fecha_facturacion).toLocaleString()}
              </p>
            </Col>
          </Row>
        </Card>
      </Space>
    );
  };

  const SerialHistory = () => (
    <Card>
      <Table
        loading={loadingHistory}
        dataSource={serialHistory}
        rowKey="id"
        columns={[
          {
            title: "Fecha",
            dataIndex: "date_created",
            key: "date_created",
            render: (date) => new Date(date).toLocaleString(),
          },
          {
            title: "Tipo",
            dataIndex: "tipo",
            key: "tipo",
            render: (tipo) => (
              <Tag color={tipo === 1 ? "blue" : tipo === 2 ? "red" : "orange"}>
                {getHistoryTypeText(tipo)}
              </Tag>
            ),
          },
          {
            title: "Serial",
            key: "serial",
            render: (_, record) => {
              // Handle array of serials
              if (record.seriales && Array.isArray(record.seriales)) {
                return record.seriales
                  .map((serial) => serial.seriales_id?.serial)
                  .join(", ");
              }
              // Handle single serial
              return record.seriales?.seriales_id?.serial || "N/A";
            },
          },
          {
            title: "Usuario",
            key: "user",
            render: (_, record) =>
              `${record.user_created?.first_name || ""} ${
                record.user_created?.last_name || ""
              }`,
          },
        ]}
        pagination={false}
      />
    </Card>
  );

  const items = [
    {
      key: "1",
      label: "Detalles de Factura",
      children: <InvoiceDetails />,
    },
    {
      key: "2",
      label: "Historial de Facturacion",
      children: <SerialHistory />,
    },
  ];

  // Modify the footer logic
  const footer = validation && selectedFactura?.estado !== "devolucion" &&
    selectedFactura?.estado !== "devolcion de factura"
    ? [
        <Button
          key="anular"
          type="primary"
          danger
          disabled={!cajaAbierta}
          onClick={() => {
            if (!cajaAbierta) {
              message.warning('Debe tener una caja abierta para anular facturas');
              return;
            }
            setIsAnularModalVisible(true);
            handleAnularClick(selectedFactura);
            fetchMotivos();
          }}
        >
          Anular factura
        </Button>,
      ]
    : null;

  // Update the Modal component
  return (
    <>
      <Modal
        title={`Detalles de Factura - ${selectedFactura?.consecutivo}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedFactura(null);
          setSerialHistory([]);
          setFacturaDetails(null);
        }}
        footer={footer}
        width={1000}
      >
        {validation && !cajaAbierta && (
          <Alert
            message="Caja Cerrada"
            description="Debe tener una caja abierta para poder anular facturas"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {selectedFactura && <Tabs defaultActiveKey="1" items={items} />}
      </Modal>

      <Modal
        title="Anular Factura"
        open={isAnularModalVisible}
        onOk={handleAnularFactura}
        onCancel={() => {
          setIsAnularModalVisible(false);
          setSelectedMotivo(null);
        }}
        confirmLoading={anularLoading}
        okText="Anular"
        cancelText="Cancelar"
      >
        <p>
          ¿Está seguro que desea anular la factura{" "}
          {selectedFactura?.consecutivo}?
        </p>
        <Select
          style={{ width: "100%" }}
          placeholder="Seleccione el motivo de anulación"
          onChange={(value) => setSelectedMotivo(value)}
          value={selectedMotivo}
        >
          {motivos.map((motivo) => (
            <Select.Option key={motivo.id} value={motivo.id}>
              {motivo.nombre}
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </>
  );
};

export default InvoiceModal;
